import React, { useState, useMemo, useEffect, useCallback } from "react";
import Select from "react-select";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { fetchWithAuth } from "../../../../utils/api";
import { swalConfirm } from "../../../../utils/swalAlert";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

// ── Step constants ──
const STEPS = {
  CONFIGURE: 0,
  PREVIEW: 1,
  EXECUTE: 2,
  RESULTS: 3,
};

const STEP_LABELS = ["Configure", "Preview", "Promoting…", "Results"];

// ── Helpers ──
const buildAcademicYearLabel = (startYear) => {
  const endYear = startYear + 1;
  const endShort = String(endYear).slice(-2);
  return `${startYear}-${endShort}`;
};

// ── Component ──
const BulkPromoteModal = ({
  isOpen,
  onClose,
  students, // all students from parent
  levelOptions,
  onPromotionComplete,
}) => {
  // ─── State ───
  const [step, setStep] = useState(STEPS.CONFIGURE);
  const [sourceGrade, setSourceGrade] = useState(null);
  const [targetGrade, setTargetGrade] = useState(null);
  const [isPassout, setIsPassout] = useState(false);
  const [academicYearStart, setAcademicYearStart] = useState(
    new Date().getFullYear(),
  );
  const [skippedStudentIds, setSkippedStudentIds] = useState(new Set());
  const [progress, setProgress] = useState({ current: 0, total: 0, name: "" });
  const [results, setResults] = useState({ success: [], failed: [] });

  // Reset everything on close / reopen
  useEffect(() => {
    if (!isOpen) {
      setStep(STEPS.CONFIGURE);
      setSourceGrade(null);
      setTargetGrade(null);
      setIsPassout(false);
      setAcademicYearStart(new Date().getFullYear());
      setSkippedStudentIds(new Set());
      setProgress({ current: 0, total: 0, name: "" });
      setResults({ success: [], failed: [] });
    }
  }, [isOpen]);

  // ─── Derived data ───

  // Only active students
  const activeStudents = useMemo(() => {
    return students.filter((s) => s.isActive !== false);
  }, [students]);

  // Source grade options — only grades that actually have students
  const sourceGradeOptions = useMemo(() => {
    const gradeMap = {};
    activeStudents.forEach((s) => {
      const lvl = s.level;
      if (lvl && lvl !== "-") {
        gradeMap[lvl] = (gradeMap[lvl] || 0) + 1;
      }
    });
    return Object.entries(gradeMap).map(([grade, count]) => ({
      value: grade,
      label: `${grade} (${count} student${count > 1 ? "s" : ""})`,
    }));
  }, [activeStudents]);

  // Target grade options — all levels + "Passout"
  const targetGradeOptions = useMemo(() => {
    const opts = levelOptions.map((opt) => ({
      value: opt.value,
      label: opt.label || opt.value,
    }));
    opts.push({
      value: "__PASSOUT__",
      label: "🎓 Passout",
    });
    return opts;
  }, [levelOptions]);

  // Academic year options
  const academicYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const opts = [];
    for (let y = currentYear - 3; y <= currentYear + 2; y++) {
      opts.push({
        value: y,
        label: buildAcademicYearLabel(y),
      });
    }
    return opts;
  }, []);

  // Students matching the source grade
  const sourceStudents = useMemo(() => {
    if (!sourceGrade) return [];
    return activeStudents.filter((s) => s.level === sourceGrade);
  }, [activeStudents, sourceGrade]);

  // Students that will actually be promoted (excluding skipped)
  const studentsToPromote = useMemo(() => {
    return sourceStudents.filter((s) => !skippedStudentIds.has(s._id));
  }, [sourceStudents, skippedStudentIds]);

  // Compute the actual new level label
  const computedTargetLabel = useMemo(() => {
    if (isPassout) {
      return `Passout_${buildAcademicYearLabel(academicYearStart)}`;
    }
    return targetGrade;
  }, [isPassout, targetGrade, academicYearStart]);

  // Can proceed from current step?
  const canProceed = useMemo(() => {
    if (step === STEPS.CONFIGURE) {
      if (!sourceGrade) return false;
      if (isPassout) return true; // academic year always has a default
      if (!targetGrade) return false;
      return true;
    }
    if (step === STEPS.PREVIEW) {
      return studentsToPromote.length > 0;
    }
    return true;
  }, [step, sourceGrade, targetGrade, isPassout, studentsToPromote.length]);

  // Check for conflict (target already has students)
  const targetConflictCount = useMemo(() => {
    if (!computedTargetLabel || isPassout) return 0;
    return activeStudents.filter(
      (s) =>
        s.level === computedTargetLabel &&
        !sourceStudents.some((ss) => ss._id === s._id),
    ).length;
  }, [computedTargetLabel, isPassout, activeStudents, sourceStudents]);

  // ─── Handlers ───

  const handleTargetChange = (opt) => {
    if (opt?.value === "__PASSOUT__") {
      setIsPassout(true);
      setTargetGrade(null);
    } else {
      setIsPassout(false);
      setTargetGrade(opt?.value || null);
    }
  };

  const toggleSkipStudent = (studentId) => {
    setSkippedStudentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.RESULTS));
  const goBack = () => setStep((s) => Math.max(s - 1, STEPS.CONFIGURE));

  // Execute promotion
  const executePromotion = useCallback(async () => {
    setStep(STEPS.EXECUTE);
    const total = studentsToPromote.length;
    setProgress({ current: 0, total, name: "" });

    const successList = [];
    const failedList = [];

    for (let i = 0; i < studentsToPromote.length; i++) {
      const student = studentsToPromote[i];
      const fullName = `${student.firstName} ${student.lastName}`;
      setProgress({ current: i + 1, total, name: fullName });

      try {
        let body;
        if (isPassout) {
          body = {
            level: "",
            isActive: false,
            inActiveReason: computedTargetLabel,
          };
        } else {
          body = { level: computedTargetLabel };
        }

        const response = await fetchWithAuth(
          `${serverUrl}/api/user/update/${student._id}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
        );

        if (response && response.ok) {
          successList.push(student);
        } else {
          const errData = await response?.json().catch(() => ({}));
          failedList.push({
            ...student,
            error: errData?.message || "Failed to update",
          });
        }
      } catch (err) {
        failedList.push({
          ...student,
          error: err.message || "Network error",
        });
      }

      // Small delay for UI feedback
      await new Promise((r) => setTimeout(r, 50));
    }

    setResults({ success: successList, failed: failedList });
    setStep(STEPS.RESULTS);
  }, [studentsToPromote, computedTargetLabel, isPassout]);

  // Confirm before executing
  const handleConfirmAndExecute = useCallback(async () => {
    const actionLabel = isPassout ? "Move to Passout" : "Promote";

    const confirmResult = await swalConfirm(
      `${actionLabel} ${studentsToPromote.length} student(s)?`,
      `This will promote ${studentsToPromote.length} student(s) from "${sourceGrade}" to "${computedTargetLabel}".`,
      `Yes, ${actionLabel}!`,
      "No, Go Back",
    );

    if (!confirmResult.isConfirmed) return;
    executePromotion();
  }, [
    isPassout,
    studentsToPromote.length,
    sourceGrade,
    computedTargetLabel,
    executePromotion,
  ]);

  // Prevent closing during execution
  const handleClose = () => {
    if (step === STEPS.EXECUTE) return;
    if (step === STEPS.RESULTS && results.success.length > 0) {
      onPromotionComplete?.();
    }
    onClose();
  };

  // ─── Render helpers ───

  const renderStepper = () => (
    <div className="bp-stepper">
      {STEP_LABELS.map((label, idx) => (
        <div
          key={idx}
          className={`bp-stepper-step ${step === idx ? "active" : ""} ${step > idx ? "completed" : ""}`}
        >
          <div className="bp-stepper-circle">
            {step > idx ? (
              <i className="fa-solid fa-check"></i>
            ) : (
              <span>{idx + 1}</span>
            )}
          </div>
          <span className="bp-stepper-label">{label}</span>
          {idx < STEP_LABELS.length - 1 && (
            <div className="bp-stepper-line"></div>
          )}
        </div>
      ))}
    </div>
  );

  // ── Step 1: Configure ──
  const renderConfigureStep = () => (
    <div className="bp-step-content">
      <div className="bp-info-banner">
        <i className="fa-solid fa-lightbulb"></i>
        <div>
          <strong>Year-End Promotion Guide</strong>
          <p>
            Start with the <b>highest grade first</b>. Move the highest grade to{" "}
            <b>Passout</b>, then promote lower grades upward in descending order
            (e.g., Grade 10 → Passout, then Grade 9 → Grade 10, etc.).
          </p>
        </div>
      </div>

      <h3 className="bp-section-title">Configure Promotion</h3>

      <div className="bp-config-form">
        {/* Source Grade */}
        <div className="bp-config-field">
          <label>
            <i className="fa-solid fa-arrow-right-from-bracket"></i> Source
            Grade
            <span className="bp-required">*</span>
          </label>
          <Select
            value={
              sourceGrade
                ? sourceGradeOptions.find((o) => o.value === sourceGrade) || {
                    value: sourceGrade,
                    label: sourceGrade,
                  }
                : null
            }
            onChange={(opt) => {
              setSourceGrade(opt?.value || null);
              setSkippedStudentIds(new Set());
            }}
            options={sourceGradeOptions}
            placeholder="Select the current grade..."
            isClearable
            classNamePrefix="react-select"
          />
          {sourceGrade && (
            <span className="bp-field-hint">
              <i
                className="fa-solid fa-users"
                style={{ marginRight: "4px" }}
              ></i>
              {sourceStudents.length} student(s) in {sourceGrade}
            </span>
          )}
        </div>

        {/* Target Grade */}
        <div className="bp-config-field">
          <label>
            <i className="fa-solid fa-arrow-right-to-bracket"></i> Target Grade
            <span className="bp-required">*</span>
          </label>
          <Select
            value={
              isPassout
                ? { value: "__PASSOUT__", label: "🎓 Passout" }
                : targetGrade
                  ? targetGradeOptions.find((o) => o.value === targetGrade) || {
                      value: targetGrade,
                      label: targetGrade,
                    }
                  : null
            }
            onChange={handleTargetChange}
            options={targetGradeOptions}
            placeholder="Select the grade to promote TO..."
            isClearable
            classNamePrefix="react-select"
          />
        </div>

        {/* Academic Year — only for Passout */}
        {isPassout && (
          <div className="bp-config-field">
            <label>
              <i className="fa-solid fa-calendar-alt"></i> Academic Year
              <span className="bp-required">*</span>
            </label>
            <Select
              value={academicYearOptions.find(
                (o) => o.value === academicYearStart,
              )}
              onChange={(opt) =>
                setAcademicYearStart(opt?.value || new Date().getFullYear())
              }
              options={academicYearOptions}
              classNamePrefix="react-select"
            />
            <span className="bp-field-hint">
              Students will be labelled as{" "}
              <strong>{computedTargetLabel}</strong> and marked inactive.
            </span>
          </div>
        )}

        {/* Arrow Preview */}
        {sourceGrade && computedTargetLabel && (
          <div className="bp-transfer-preview">
            <div className="bp-transfer-side from">
              <i className="fa-solid fa-users"></i>
              <span>{sourceGrade}</span>
            </div>
            <div className="bp-transfer-arrow">
              <i className="fa-solid fa-arrow-right"></i>
            </div>
            <div className="bp-transfer-side to">
              <i
                className={`fa-solid ${
                  isPassout ? "fa-graduation-cap" : "fa-arrow-up"
                }`}
              ></i>
              <span>{computedTargetLabel}</span>
              {targetConflictCount > 0 && (
                <small className="conflict">
                  {targetConflictCount} existing
                </small>
              )}
            </div>
          </div>
        )}

        {/* Conflict Warning */}
        {targetConflictCount > 0 && !isPassout && (
          <div className="bp-warning-banner">
            <i className="fa-solid fa-triangle-exclamation"></i>
            <div>
              <strong>Heads Up!</strong>
              <p>
                <strong>{computedTargetLabel}</strong> already has{" "}
                <strong>{targetConflictCount}</strong> student(s). Proceeding
                will place both batches under the same grade. Consider moving
                existing {computedTargetLabel} students to any other grade or
                Passout.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Step 2: Preview ──
  const renderPreviewStep = () => (
    <div className="bp-step-content">
      <h3 className="bp-section-title">Review & Select Students</h3>

      <div className="bp-preview-summary">
        <div className="bp-preview-badge">
          <span className="bp-badge-label">From</span>
          <span className="bp-badge-value">{sourceGrade}</span>
        </div>
        <div className="bp-preview-badge">
          <span className="bp-badge-label">To</span>
          <span
            className={`bp-badge-value ${isPassout ? "passout" : "promote"}`}
          >
            {computedTargetLabel}
          </span>
        </div>
        <div className="bp-preview-badge">
          <span className="bp-badge-label">Promoting</span>
          <span className="bp-badge-value">
            {studentsToPromote.length} / {sourceStudents.length}
          </span>
        </div>
      </div>

      {skippedStudentIds.size > 0 && (
        <div
          className="bp-info-banner"
          style={{ marginBottom: "0.75rem", background: "#fff8e1" }}
        >
          <i
            className="fa-solid fa-user-minus"
            style={{ color: "#f59e0b" }}
          ></i>
          <div>
            <strong>{skippedStudentIds.size} student(s) skipped</strong>
            <p style={{ margin: 0 }}>
              Unchecked students will not be promoted. You can re-check them to
              include.
            </p>
          </div>
        </div>
      )}

      <div className="bp-preview-table-wrapper">
        <table className="bp-preview-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Reference ID</th>
              <th>Current Level</th>
              <th>New Level</th>
              <th style={{ width: "50px", textAlign: "center" }}>Include</th>
            </tr>
          </thead>
          <tbody>
            {sourceStudents.map((stu, idx) => {
              const isSkipped = skippedStudentIds.has(stu._id);
              return (
                <tr
                  key={stu._id}
                  className={isSkipped ? "skipped-row" : ""}
                  style={{
                    opacity: isSkipped ? 0.5 : 1,
                    textDecoration: isSkipped ? "line-through" : "none",
                  }}
                >
                  <td>{idx + 1}</td>
                  <td>
                    {stu.firstName} {stu.lastName}
                  </td>
                  <td>{stu.referenceId || "-"}</td>
                  <td>
                    <span className="bp-level-badge current">
                      {stu.level || "-"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`bp-level-badge new ${isPassout ? "passout" : "promote"}`}
                    >
                      {isSkipped ? "-" : computedTargetLabel}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <label
                      className="filter-toggle"
                      style={{
                        margin: "0 auto",
                        display: "flex",
                        justifyContent: "center",
                      }}
                      title={
                        isSkipped
                          ? "Click to include this student"
                          : "Click to skip this student"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={!isSkipped}
                        onChange={() => toggleSkipStudent(stu._id)}
                      />
                      <span className="toggle-track">
                        <span className="toggle-indicator"></span>
                      </span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {targetConflictCount > 0 && !isPassout && (
        <div className="bp-warning-banner" style={{ marginTop: "1rem" }}>
          <i className="fa-solid fa-triangle-exclamation"></i>
          <div>
            <strong>⚠️ Warning:</strong> {computedTargetLabel} already has{" "}
            {targetConflictCount} student(s). Proceeding will mix both batches
            under the same grade. Do you want to continue?
          </div>
        </div>
      )}
    </div>
  );

  // ── Step 3: Execution ──
  const renderExecuteStep = () => (
    <div className="bp-step-content">
      <div className="bp-execute-overlay">
        <div className="bp-execute-content">
          <Loader style={{ fontSize: "3rem", left: "0" }} />
          <h3>{isPassout ? "Moving to Passout…" : "Promoting Students…"}</h3>
          <p className="bp-progress-text">
            Processing <strong>{progress.name}</strong>
          </p>
          <p className="bp-progress-count">
            {progress.current} of {progress.total}
          </p>
          <div className="bp-progress-bar-container">
            <div
              className="bp-progress-bar-fill"
              style={{
                width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // ── Step 4: Results ──
  const renderResultsStep = () => (
    <div className="bp-step-content">
      {(results.success.length > 0 || results.failed.length > 0) && (
        <>
          <div className="bp-results-stats">
            <div className="bp-stat success">
              <i className="fa-solid fa-circle-check"></i>
              <span className="bp-stat-num">{results.success.length}</span>
              <span className="bp-stat-label">Successful</span>
            </div>
            {results.failed.length > 0 && (
              <div className="bp-stat failed">
                <i className="fa-solid fa-circle-xmark"></i>
                <span className="bp-stat-num">{results.failed.length}</span>
                <span className="bp-stat-label">Failed</span>
              </div>
            )}
          </div>

          {/* Results Table */}
          <div className="bp-preview-table-wrapper">
            <table className="bp-preview-table results">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th>Student Name</th>
                  <th>Change</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {results.success.map((stu) => (
                  <tr key={stu._id} className="success-row">
                    <td>
                      <i
                        className="fa-solid fa-circle-check"
                        style={{ color: "#16a34a" }}
                      ></i>
                    </td>
                    <td>
                      {stu.firstName} {stu.lastName}
                    </td>
                    <td>
                      <span className="bp-level-badge current">
                        {stu.level || "-"}
                      </span>
                      <i
                        className="fa-solid fa-arrow-right"
                        style={{
                          margin: "0 0.4rem",
                          fontSize: "0.7rem",
                          color: "#94a3b8",
                        }}
                      ></i>
                      <span
                        className={`bp-level-badge new ${isPassout ? "passout" : "promote"}`}
                      >
                        {computedTargetLabel}
                      </span>
                    </td>
                    <td>
                      <span className="bp-status-badge success">Done</span>
                    </td>
                  </tr>
                ))}
                {results.failed.map((stu) => (
                  <tr key={stu._id} className="failed-row">
                    <td>
                      <i
                        className="fa-solid fa-circle-xmark"
                        style={{ color: "#dc2626" }}
                      ></i>
                    </td>
                    <td>
                      {stu.firstName} {stu.lastName}
                    </td>
                    <td>—</td>
                    <td>
                      <span className="bp-status-badge failed">
                        {stu.error}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  // ─── Main Render ───
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth={"95%"}
      closeButton={step !== STEPS.EXECUTE}
    >
      <div className="bp-modal-wrapper">
        {/* Header */}
        <div className="bp-header">
          <h2>
            <i className="fa-solid fa-users-gear"></i> Promote Students
          </h2>
        </div>

        {/* Stepper */}
        {renderStepper()}

        {/* Step Content */}
        {step === STEPS.CONFIGURE && renderConfigureStep()}
        {step === STEPS.PREVIEW && renderPreviewStep()}
        {step === STEPS.EXECUTE && renderExecuteStep()}
        {step === STEPS.RESULTS && renderResultsStep()}

        {/* Footer Navigation */}
        {step !== STEPS.EXECUTE && (
          <div className="bp-footer">
            {step > STEPS.CONFIGURE && step < STEPS.RESULTS && (
              <button className="bp-btn secondary" onClick={goBack}>
                <i className="fa-solid fa-arrow-left"></i> Back
              </button>
            )}

            <div className="bp-footer-right">
              {step === STEPS.CONFIGURE && (
                <button
                  className="bp-btn primary"
                  disabled={!canProceed}
                  onClick={goNext}
                >
                  Continue <i className="fa-solid fa-arrow-right"></i>
                </button>
              )}

              {step === STEPS.PREVIEW && (
                <button
                  className="bp-btn primary confirm"
                  disabled={!canProceed}
                  onClick={handleConfirmAndExecute}
                >
                  <i className="fa-solid fa-check"></i> Confirm &{" "}
                  {isPassout ? "Move to Passout" : "Promote"} (
                  {studentsToPromote.length})
                </button>
              )}

              {step === STEPS.RESULTS && (
                <button className="bp-btn primary" onClick={handleClose}>
                  Done <i className="fa-solid fa-check"></i>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkPromoteModal;
